import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="22460" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-bold uppercase tracking-tight text-foreground/90 transition-colors hover:text-primary py-2
                  after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:bg-primary 
                  after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-bold uppercase tracking-tight text-foreground hover:text-primary transition-colors border-b border-border/50 pb-2" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/10 shadow-lg" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex-shrink-0 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591728/img/techfix_logo_primary.png" alt="Olive Hill Equipment Service Logo" className="h-7 w-auto object-contain brightness-110" />
        </Link>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="2" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-4 flex-shrink-0">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-all duration-200 border border-primary/20 rounded-sm font-bold uppercase tracking-tight text-sm">
            <Link to="/RequestServicePage">Request Repair</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-20 left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl animate-in slide-in-from-top-5 duration-200">
          <MenuProvider menu_id="2" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-6 pt-0 flex flex-col gap-4">
            <Button asChild className="w-full bg-primary text-primary-foreground shadow-md rounded-sm font-bold uppercase tracking-tight">
              <Link to="/RequestServicePage" onClick={() => setIsMobileMenuOpen(false)}>
                Request Repair
              </Link>
            </Button>
          </div>
        </div>}
    </header>;
}